import type { NestedDescription, StatementCardShape } from "./aboxViewModel";

type Row = NestedDescription["rows"][number];
type Value = Row["values"][number];

/** One participant: who, and in which role; either may be missing from the data. */
export interface Participant {
  readonly actor: Value | null;
  readonly role: Value | null;
}

/**
 * One statement as Daniel's viewer reads it (`regest_viewer.py` render_statement): what it is
 * about, an event or a state, whose class is its kind; who takes part in which role; then the
 * rest of what it says, the event's or state's rows before the statement's own.
 */
export interface StatementCard {
  readonly about: NestedDescription;
  readonly participants: readonly Participant[];
  readonly rows: readonly Row[];
}

/**
 * The card of what a statement says (`nestSaid`'s first root), by the project's profile; `null`
 * when the profile shapes no card or the statement names nothing by its content properties, which
 * then shows as written.
 */
export function statementCard(main: NestedDescription | undefined, shape: StatementCardShape | null): StatementCard | null {
  if (main === undefined || shape === null) return null;
  const contentRow = main.rows.find((row) => shape.content.has(row.property) && row.values.some((value) => value.kind === "nested"));
  const about = contentRow?.values.find((value) => value.kind === "nested");
  if (contentRow === undefined || about?.kind !== "nested") return null;
  const participation = shape.participation;
  // A participation reads as one line only when it says no more than its actor and its role, each once and not
  // described further; any other stays a row as written, so the card drops nothing the statement says (Sol R6).
  const asLine = (value: Value): Participant | null => {
    if (participation === null || value.kind !== "nested") return null;
    const { rows } = value.description;
    const plain = (inner: Row) => inner.values.length === 1 && (inner.values[0]?.kind !== "nested" || inner.values[0].description.rows.length === 0);
    if (!rows.every((inner) => (inner.property === participation.actor || inner.property === participation.role) && plain(inner))) return null;
    const valueOf = (property: string) => rows.find((inner) => inner.property === property)?.values[0] ?? null;
    return { actor: valueOf(participation.actor), role: valueOf(participation.role) };
  };
  const participants: Participant[] = [];
  const contentRows = about.description.rows.flatMap((row): Row[] => {
    if (participation === null || row.property !== participation.property) return [row];
    const kept = row.values.filter((value) => {
      const line = asLine(value);
      if (line !== null) participants.push(line);
      return line === null;
    });
    return kept.length === 0 ? [] : [{ property: row.property, values: kept }];
  });
  const restOfContent = contentRow.values.filter((value) => value !== about);
  const ownRows = main.rows.flatMap((row) => (row !== contentRow ? [row] : restOfContent.length === 0 ? [] : [{ property: row.property, values: restOfContent }]));
  return { about: about.description, participants, rows: [...contentRows, ...ownRows] };
}
